/**
 * EVENTSFLO-ADMIN-BACKUP-UI-COMPONENT-SPLIT-V1
 *
 * Right-side inspection drawer with 6 tabs. The 5 read-only tabs
 * (Manifest, Schema, Inventory, GDPR, Audit) lazy-fetch their endpoint
 * exactly like V1 did. The 6th tab (Restore Safety) is delegated to
 * `BackupRestoreSafetyTab` which exposes ONLY preflight + dry-run.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { JsonBlock } from "./backupFormatters";
import { inspectionTabFetcher } from "./backupApi";
import BackupRestoreSafetyTab from "./BackupRestoreSafetyTab";

const DATA_TABS = ["manifest", "schema", "inventory", "gdpr", "audit"];

// Per-tab data-testid used by the JsonBlock "Copy JSON" button so each
// inspect tab gets a unique selector (per EVENTSFLO-ADMIN-BACKUP-UI-
// COPY-JSON-V1).
const COPY_TESTID = {
  manifest: "copy-json-manifest",
  schema: "copy-json-schema",
  inventory: "copy-json-inventory",
  gdpr: "copy-json-gdpr",
  audit: "copy-json-audit",
};

export default function BackupInspectDrawer({ backup, onOpenChange }) {
  const [activeTab, setActiveTab] = useState("manifest");
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});

  // Reset when targeting a new archive (V1 behaviour).
  useEffect(() => {
    if (backup) {
      setActiveTab("manifest");
      setTabData({});
      setTabLoading({});
    }
  }, [backup]);

  const loadTab = useCallback(
    async (tab) => {
      if (!backup || tab === "restore" || tabData[tab] !== undefined) return;
      const fetcher = inspectionTabFetcher[tab];
      if (!fetcher) return;
      setTabLoading((s) => ({ ...s, [tab]: true }));
      try {
        const { data } = await fetcher(backup.backup_id);
        setTabData((s) => ({ ...s, [tab]: data }));
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Unknown error";
        setTabData((s) => ({
          ...s,
          [tab]: { __error: String(msg), __status: e?.response?.status },
        }));
      } finally {
        setTabLoading((s) => ({ ...s, [tab]: false }));
      }
    },
    [backup, tabData],
  );

  useEffect(() => {
    if (backup && activeTab !== "restore") loadTab(activeTab);
  }, [backup, activeTab, loadTab]);

  return (
    <Sheet open={!!backup} onOpenChange={(o) => !o && onOpenChange(false)}>
      <SheetContent className="sm:max-w-2xl w-full overflow-auto" data-testid="inspect-drawer">
        <SheetHeader>
          <SheetTitle>Inspect backup</SheetTitle>
          <SheetDescription className="font-mono text-xs">
            {backup?.filename}
          </SheetDescription>
        </SheetHeader>
        {backup && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid grid-cols-6">
              <TabsTrigger value="manifest" data-testid="tab-manifest">Manifest</TabsTrigger>
              <TabsTrigger value="schema" data-testid="tab-schema">Schema</TabsTrigger>
              <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
              <TabsTrigger value="gdpr" data-testid="tab-gdpr">GDPR</TabsTrigger>
              <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
              <TabsTrigger value="restore" data-testid="tab-restore">Restore</TabsTrigger>
            </TabsList>

            {DATA_TABS.map((t) => (
              <TabsContent key={t} value={t} className="mt-3">
                {tabLoading[t] ? (
                  <div className="text-sm text-zinc-400">Loading…</div>
                ) : tabData[t]?.__error ? (
                  <div className="text-sm text-red-400">
                    {tabData[t].__status} — {tabData[t].__error}
                  </div>
                ) : tabData[t] ? (
                  <JsonBlock data={tabData[t]} copyTestId={COPY_TESTID[t]} />
                ) : (
                  <div className="text-sm text-zinc-400">Click the tab to load.</div>
                )}
              </TabsContent>
            ))}

            <TabsContent value="restore">
              <BackupRestoreSafetyTab backupId={backup.backup_id} />
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
